import dayjs from 'dayjs';
import type { StatusTone } from '@/components/common/StatusChip';
import { TRACKING_STATUS_TONES } from '@/components/common/trackingTones';
import type {
  FeedState,
  TrackedVehicle,
  TrackingFeed,
  TrackingOverview,
  TrackingSensor,
  TrackingStatus,
} from '@/types/api';

/**
 * Every rule the fleet map follows, as pure functions, so each one is tested
 * without a map. The design is `docs/tracking-design-review.md` in the backend
 * (§4, §5, §14, §15); the one sentence that governs all of it is **a stale map
 * that looks live is the worst outcome.**
 */

/**
 * How often the map asks for a fresh snapshot: **10 s**, and only while the
 * tab is visible.
 *
 * - The backend's feed polls Wialon every ~5 s and the API only reads the
 *   table that feed writes, so asking faster than that buys nothing — the
 *   same snapshot twice.
 * - The vehicles themselves report far more slowly: the platform's van, parked
 *   and asleep, on a fixed **10-minute** grid (each report landing a minute or
 *   so late); a moving tracker typically every few seconds to a minute. 10 s
 *   is well inside the moving case and costs one indexed read per poll.
 * - Freshness of the *feed* is what 10 s really protects. The backend calls
 *   the feed stale after 60 s without a successful Wialon poll; at 10 s the map
 *   learns that within ~70 s and says so, instead of showing pins that stopped
 *   being true a minute ago.
 *
 * The design proposed the same figure (§1). Push (SSE) is a seam for later.
 */
export const TRACKING_POLL_MS = 10_000;

/** One vehicle on its own: a neighbourhood, not a street. */
export const SINGLE_VEHICLE_ZOOM = 13;
/** Several vehicles: fit them all, but never closer than this. */
export const FIT_MAX_ZOOM = 14;

/**
 * The status chip's tone: `TRACKING_STATUS_TONES`, shared with the vehicles
 * list so the two say a status alike. `UNKNOWN` (our feed down) is grey and
 * `OFFLINE` (the van gone quiet) is red — never the same look.
 */
const TONES = TRACKING_STATUS_TONES;

export function statusTone(status?: TrackingStatus | null): StatusTone {
  return status ? TONES[status] : 'neutral';
}

/** A vehicle the map can place: it has a position with real coordinates. */
export function isMappable(
  vehicle: TrackedVehicle,
): vehicle is TrackedVehicle & { position: { latitude: number; longitude: number } } {
  const position = vehicle.position;
  return (
    position != null && Number.isFinite(position.latitude) && Number.isFinite(position.longitude)
  );
}

/**
 * `LAST_KNOWN` means the tracker's newest report had **no satellite fix**: the
 * coordinates are where it was last known to be, not where it is. The owner's
 * ruling (2026-10-20) is to show it, marked, and visibly different from a fix.
 */
export function isLastKnown(vehicle: Pick<TrackedVehicle, 'position'>): boolean {
  return vehicle.position?.source === 'LAST_KNOWN';
}

/**
 * How a pin is drawn. Two independent questions, each with its own channel:
 *
 * - **What state is the van in** — the fill. A known state is a solid dot in
 *   its tone. `OFFLINE` is hollow: the van stopped talking. `UNKNOWN` is grey
 *   and hatched with a question mark: we stopped being able to see.
 * - **How sure is the position** — the ring. A satellite fix is a solid white
 *   ring; `LAST_KNOWN` is a **dashed** ring around a hollow centre, and the
 *   label under it says "last known location" in words.
 *
 * Nothing about a marker is ever animated.
 */
export interface MarkerLook {
  tone: StatusTone;
  fill: 'solid' | 'hollow' | 'hatched';
  ring: 'solid' | 'dashed';
  glyph: '?' | null;
  lastKnown: boolean;
}

export function markerLook(vehicle: Pick<TrackedVehicle, 'status' | 'position'>): MarkerLook {
  const lastKnown = isLastKnown(vehicle);
  const status = vehicle.status;
  if (status === 'UNKNOWN') {
    return {
      tone: 'neutral',
      fill: 'hatched',
      ring: lastKnown ? 'dashed' : 'solid',
      glyph: '?',
      lastKnown,
    };
  }
  return {
    tone: statusTone(status),
    fill: status === 'OFFLINE' || lastKnown ? 'hollow' : 'solid',
    ring: lastKnown ? 'dashed' : 'solid',
    glyph: null,
    lastKnown,
  };
}

/**
 * What the map says about **our** connection to Wialon, or `null` while it is
 * live. Read `live`, never `state`: a feed that died cannot update its own
 * state, so `state: LIVE` with `live: false` is exactly the case of a dead
 * poller and has its own sentence.
 */
export interface FeedNotice {
  /** i18n key under `tracking.feed`. */
  key: `tracking.feed.${FeedState | 'STALLED'}`;
  /** The last successful poll — positions are as of this, at best. */
  since?: string;
  /** For a human reading the detail; never switched on. */
  errorCode?: string;
}

export function feedNotice(feed?: TrackingFeed | null): FeedNotice | null {
  if (!feed) return { key: 'tracking.feed.STALLED' };
  if (feed.live) return null;
  const state = feed.state && feed.state !== 'LIVE' ? feed.state : 'STALLED';
  return {
    key: `tracking.feed.${state}`,
    since: feed.lastSuccessAt,
    errorCode: feed.lastErrorCode,
  };
}

/**
 * Whether the map as a whole must be drawn as stale: the backend says its feed
 * is not live, **or** we could not fetch a fresh snapshot ourselves. The
 * second matters as much as the first — a poll that fails leaves the last
 * answer on screen, ages frozen, looking exactly as live as it did.
 */
export function mapIsStale(feed: TrackingFeed | null | undefined, refreshFailed: boolean): boolean {
  return refreshFailed || feedNotice(feed) !== null;
}

/** Where the camera goes, decided without a map so it can be tested. */
export type CameraPlan =
  | { kind: 'country' }
  | { kind: 'point'; longitude: number; latitude: number; zoom: number }
  | { kind: 'bounds'; west: number; south: number; east: number; north: number; maxZoom: number };

export function cameraPlan(points: { latitude: number; longitude: number }[]): CameraPlan {
  if (points.length === 0) return { kind: 'country' };
  const longitudes = points.map((p) => p.longitude);
  const latitudes = points.map((p) => p.latitude);
  const west = Math.min(...longitudes);
  const east = Math.max(...longitudes);
  const south = Math.min(...latitudes);
  const north = Math.max(...latitudes);
  // One vehicle, or several parked in the same yard: a bounding box of zero
  // size would zoom to the maximum, which is a rooftop, not a place.
  if (points.length === 1 || (east - west < 1e-4 && north - south < 1e-4)) {
    return { kind: 'point', longitude: west, latitude: south, zoom: SINGLE_VEHICLE_ZOOM };
  }
  return { kind: 'bounds', west, south, east, north, maxZoom: FIT_MAX_ZOOM };
}

/**
 * A sensor's value as text. **`null` is "no reading"** — Wialon's
 * not-available — and renders as an em dash, never as 0. A value is a decimal
 * string and is shown exactly as sent: nothing here parses it.
 */
export function sensorReading(sensor: Pick<TrackingSensor, 'value' | 'unit'>): {
  value: string;
  unit: string | null;
  missing: boolean;
} {
  if (sensor.value == null || sensor.value === '') return { value: '—', unit: null, missing: true };
  return { value: sensor.value, unit: sensor.unit?.trim() || null, missing: false };
}

/**
 * The icon family for a sensor, by its Wialon **`type`** — never its name or
 * id, which are free text and differ per unit. Unknown types are generic.
 */
export type SensorKind = 'temperature' | 'voltage' | 'fuel' | 'ignition' | 'digital' | 'other';

export function sensorKind(type?: string | null): SensorKind {
  switch ((type ?? '').trim().toLowerCase()) {
    case 'temperature':
      return 'temperature';
    case 'voltage':
      return 'voltage';
    case 'fuel level':
      return 'fuel';
    case 'engine operation':
      return 'ignition';
    case 'digital':
      return 'digital';
    default:
      return 'other';
  }
}

/** i18n key for the ignition line. Without a sensor there is no `IDLE`, and the screen says so. */
export function ignitionKey(
  vehicle: Pick<TrackedVehicle, 'ignitionReported' | 'ignition'>,
): string | null {
  if (!vehicle.ignitionReported) return 'tracking.ignition.notReported';
  if (vehicle.ignition == null) return null;
  return vehicle.ignition ? 'tracking.ignition.on' : 'tracking.ignition.off';
}

/**
 * A recorded time, as a clock time when it falls on the same day as the
 * snapshot and with its date otherwise. "Same day" is judged against the
 * server's `generatedAt`, not the browser's today.
 */
export function clockLabel(at?: string | null, reference?: string | null): string {
  if (!at) return '—';
  const sameDay =
    reference != null && dayjs(at).format('YYYY-MM-DD') === dayjs(reference).format('YYYY-MM-DD');
  return sameDay ? dayjs(at).format('HH:mm') : dayjs(at).format('YYYY-MM-DD HH:mm');
}

/**
 * How a "last known" line is set, beside `LastKnownIcon`: weight, never a
 * slant — Arabic and Kurdish have no italic (see `LastKnown.tsx`).
 */
export const lastKnownSx = { fontWeight: 600 } as const;

/**
 * The status chips over the map, in the legend's order (owner, canvas v28).
 * `UNKNOWN` is deliberately **not** a chip: it is our feed being down, which
 * is one fact about the whole map and is said once, as a banner — a chip
 * would make it look like a property some vans have. `STOPPED` is the
 * "parked" of the boards; the word on screen is the status's own label, so
 * the chip, the pin and the panel all say the same thing.
 */
export const STATUS_CHIPS = ['MOVING', 'IDLE', 'STOPPED', 'OFFLINE', 'NO_DATA', 'NO_TRACKER'] as const;
export type StatusChipValue = (typeof STATUS_CHIPS)[number];

export function isStatusChip(value: string | null | undefined): value is StatusChipValue {
  return (STATUS_CHIPS as readonly string[]).includes(value ?? '');
}

/**
 * How many vehicles each chip stands for. The overview is the **whole** fleet
 * in one answer, not a page, so counting it here is counting the fleet.
 * Vehicles with no tracker arrive in their own list and count as
 * `NO_TRACKER`; `all` is every vehicle the snapshot names.
 */
export function statusCounts(
  overview: Pick<TrackingOverview, 'vehicles' | 'vehiclesWithoutTracker'>,
): Record<StatusChipValue, number> & { all: number } {
  const counts = Object.fromEntries(STATUS_CHIPS.map((status) => [status, 0])) as Record<
    StatusChipValue,
    number
  >;
  const vehicles = overview.vehicles ?? [];
  for (const vehicle of vehicles) {
    if (isStatusChip(vehicle.status)) counts[vehicle.status] += 1;
  }
  counts.NO_TRACKER += overview.vehiclesWithoutTracker?.length ?? 0;
  return { ...counts, all: vehicles.length + (overview.vehiclesWithoutTracker?.length ?? 0) };
}

/**
 * The vehicles the status chip leaves on the map. The search is not matched
 * here: the server applies it (`GET /tracking/vehicles?search=`, C75), with
 * the Arabic-variant folding a browser match never had.
 */
export function filterTracked(
  vehicles: TrackedVehicle[],
  { status }: { status?: StatusChipValue | null },
): TrackedVehicle[] {
  return vehicles.filter((vehicle) => !status || vehicle.status === status);
}
